import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import {
  Plus, 
  Edit2, 
  Trash2,
  Globe,
  GripVertical,
  ExternalLink,
  Save,
  X,
  UserCheck,
  UserX,
  Clock,
  Shield,
  User,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  CalendarDays,
  Link2,
  Key,
  Copy,
  Eye,
  EyeOff,
  Info,
  Upload,
  Package,
  FileSpreadsheet,
  CheckSquare,
  Network,
  FileUp,
  Calendar,
  ShoppingCart,
  BarChart3,
  Wrench
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import RichTextEditor from '../components/RichTextEditor';

const API = '/api';

const ICON_OPTIONS = [
  'Mail', 'Calendar', 'Users', 'FileText', 'Globe', 'Briefcase',
  'ExternalLink', 'Clock'
];

function AdminBubblesTab() {
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBubble, setEditingBubble] = useState(null);
  const [formData, setFormData] = useState({ title: '', url: '', icon: 'Globe', order: 0 });
  const [submitting, setSubmitting] = useState(false);

  const fetchBubbles = async () => {
    try {
      const { data } = await axios.get(`${API}/bubbles`, { withCredentials: true });
      setBubbles(data);
    } catch (error) {
      console.error('Error fetching bubbles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBubbles();
  }, []);

  const openCreateDialog = () => {
    setEditingBubble(null);
    setFormData({ title: '', url: '', icon: 'Globe', order: bubbles.length });
    setDialogOpen(true);
  };

  const openEditDialog = (bubble) => {
    setEditingBubble(bubble);
    setFormData({ title: bubble.title, url: bubble.url, icon: bubble.icon, order: bubble.order });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.url.trim()) {
      toast.error('Vyplňte všechna povinná pole');
      return;
    }

    setSubmitting(true);
    try {
      if (editingBubble) {
        await axios.put(`${API}/bubbles/${editingBubble.id}`, formData, { withCredentials: true });
        toast.success('Odkaz byl upraven');
      } else {
        await axios.post(`${API}/bubbles`, formData, { withCredentials: true });
        toast.success('Odkaz byl vytvořen');
      }
      setDialogOpen(false);
      fetchBubbles();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Operace se nezdařila');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bubbleId) => {
    if (!window.confirm('Opravdu chcete smazat tento odkaz?')) return;
    
    try {
      await axios.delete(`${API}/bubbles/${bubbleId}`, { withCredentials: true });
      toast.success('Odkaz byl smazán');
      fetchBubbles();
    } catch (error) {
      toast.error('Nepodařilo se smazat odkaz');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
          Rozcestník (Bubliny)
        </h2>
        <Button
          onClick={openCreateDialog}
          data-testid="create-bubble-btn"
          className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl shadow-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nový odkaz
        </Button>
      </div>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám...</div>
          ) : bubbles.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <TableHead className="text-[#57534E]">Pořadí</TableHead>
                  <TableHead className="text-[#57534E]">Název</TableHead>
                  <TableHead className="text-[#57534E]">URL</TableHead>
                  <TableHead className="text-[#57534E]">Ikona</TableHead>
                  <TableHead className="text-[#57534E] text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bubbles.map((bubble) => (
                  <TableRow key={bubble.id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]" data-testid={`bubble-row-${bubble.id}`}>
                    <TableCell className="text-[#1C1917]">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-[#94A3B8]" />
                        {bubble.order}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#1C1917] font-medium">{bubble.title}</TableCell>
                    <TableCell>
                      <a 
                        href={bubble.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#16A34A] hover:text-[#15803D] flex items-center gap-1"
                      >
                        {bubble.url.substring(0, 30)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    <TableCell className="text-[#57534E]">{bubble.icon}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(bubble)}
                          data-testid={`edit-bubble-${bubble.id}`}
                          className="text-[#57534E] hover:text-[#16A34A]"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(bubble.id)}
                          data-testid={`delete-bubble-${bubble.id}`}
                          className="text-[#57534E] hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-[#57534E]">
              Zatím nemáte žádné odkazy
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1C1917]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingBubble ? 'Upravit odkaz' : 'Nový odkaz'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Název *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="např. Email"
                data-testid="bubble-title-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                data-testid="bubble-url-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Ikona</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger data-testid="bubble-icon-select" className="bg-white border-[#E2E8F0] text-[#1C1917]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0]">
                  {ICON_OPTIONS.map((icon) => (
                    <SelectItem key={icon} value={icon} className="text-[#1C1917] focus:bg-[#F0FDF4] focus:text-[#16A34A]">
                      {icon}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pořadí</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                data-testid="bubble-order-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#E2E8F0] text-[#57534E] hover:bg-[#F8FAF8]"
            >
              Zrušit
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-bubble-btn"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
            >
              {submitting ? 'Ukládám...' : editingBubble ? 'Uložit' : 'Vytvořit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminBlogTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/blog`, { withCredentials: true });
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({ title: post.title, content: post.content });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Vyplňte všechna pole');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/blog/${editingPost.id}`, formData, { withCredentials: true });
      toast.success('Článek byl upraven');
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error('Nepodařilo se upravit článek');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Opravdu chcete smazat tento článek?')) return;
    
    try {
      await axios.delete(`${API}/blog/${postId}`, { withCredentials: true });
      toast.success('Článek byl smazán');
      fetchPosts();
    } catch (error) {
      toast.error('Nepodařilo se smazat článek');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Správa blogu
      </h2>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám...</div>
          ) : posts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <TableHead className="text-[#57534E]">Nadpis</TableHead>
                  <TableHead className="text-[#57534E]">Autor</TableHead>
                  <TableHead className="text-[#57534E]">Datum</TableHead>
                  <TableHead className="text-[#57534E] text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post.id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]" data-testid={`blog-row-${post.id}`}>
                    <TableCell className="text-[#1C1917] font-medium max-w-xs truncate">{post.title}</TableCell>
                    <TableCell className="text-[#57534E]">{post.author_name}</TableCell>
                    <TableCell className="text-[#57534E]">
                      {new Date(post.created_at).toLocaleDateString('cs-CZ')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(post)}
                          data-testid={`edit-post-${post.id}`}
                          className="text-[#57534E] hover:text-[#16A34A]"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          data-testid={`delete-post-${post.id}`}
                          className="text-[#57534E] hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-[#57534E]">
              Zatím nemáte žádné články
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1C1917] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Upravit článek</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nadpis</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="edit-post-title-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Obsah</Label>
              <RichTextEditor
                content={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#E2E8F0] text-[#57534E] hover:bg-[#F8FAF8]"
            >
              Zrušit
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="save-post-btn"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
            >
              {submitting ? 'Ukládám...' : 'Uložit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [profileForm, setProfileForm] = useState({ department: '', position: '', phone: '', office: '' });
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, { withCredentials: true });
      toast.success('Uživatel byl schválen');
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se schválit uživatele');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/reject`, {}, { withCredentials: true });
      toast.success('Uživatel byl zamítnut');
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se zamítnout uživatele');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Opravdu chcete smazat tohoto uživatele? Tato akce je nevratná.')) return;
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Uživatel byl smazán');
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Nepodařilo se smazat uživatele');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditProfile = (user) => {
    setEditingUser(user);
    setProfileForm({
      department: user.department || '',
      position: user.position || '',
      phone: user.phone || '',
      office: user.office || '',
    });
    setProfileDialogOpen(true);
  };

  const handleToggleOverviews = async (userId, currentValue) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/permissions`, 
        { can_view_overviews: !currentValue }, 
        { withCredentials: true }
      );
      toast.success(currentValue ? 'Přístup k přehledům odebrán' : 'Přístup k přehledům udělen');
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se změnit oprávnění');
    }
  };

  const handleToggleBlog = async (userId, currentValue) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/permissions`, 
        { can_write_blog: !currentValue }, 
        { withCredentials: true }
      );
      toast.success(currentValue ? 'Oprávnění psát blog odebráno' : 'Oprávnění psát blog uděleno');
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se změnit oprávnění');
    }
  };

  const handleToggleIT = async (userId, currentValue) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/permissions`,
        { can_manage_it: !currentValue },
        { withCredentials: true }
      );
      toast.success(currentValue ? 'IT správce odebráno' : 'IT správce uděleno');
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se změnit oprávnění');
    }
  };

  const handleProfileSave = async () => {
    setProfileSubmitting(true);
    try {
      const data = {};
      if (profileForm.department.trim()) data.department = profileForm.department.trim();
      if (profileForm.position.trim()) data.position = profileForm.position.trim();
      if (profileForm.phone.trim()) data.phone = profileForm.phone.trim();
      if (profileForm.office.trim()) data.office = profileForm.office.trim();
      
      await axios.put(`${API}/admin/users/${editingUser.id}/profile`, data, { withCredentials: true });
      toast.success('Profil byl aktualizován');
      setProfileDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Nepodařilo se aktualizovat profil');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const statusConfig = {
    pending: { label: 'Čeká', className: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]', icon: Clock },
    approved: { label: 'Schváleno', className: 'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]', icon: UserCheck },
    rejected: { label: 'Zamítnuto', className: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]', icon: UserX },
  };

  const pendingUsers = users.filter(u => u.status === 'pending');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Správa uživatelů
      </h2>

      {pendingUsers.length > 0 && (
        <Card className="bg-white border-[#F59E0B]/30 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-[#92400E] font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Čekající na schválení ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <TableHead className="text-[#57534E]">Jméno</TableHead>
                  <TableHead className="text-[#57534E]">Email</TableHead>
                  <TableHead className="text-[#57534E]">Datum registrace</TableHead>
                  <TableHead className="text-[#57534E] text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.id} className="border-[#E2E8F0] hover:bg-[#FEF3C7]/20" data-testid={`pending-user-row-${user.id}`}>
                    <TableCell className="text-[#1C1917] font-medium">{user.name}</TableCell>
                    <TableCell className="text-[#57534E]">{user.email}</TableCell>
                    <TableCell className="text-[#57534E]">
                      {new Date(user.created_at).toLocaleDateString('cs-CZ')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(user.id)}
                          disabled={actionLoading === user.id}
                          data-testid={`approve-user-${user.id}`}
                          className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl text-xs px-3"
                        >
                          <UserCheck className="w-3.5 h-3.5 mr-1" />
                          Schválit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(user.id)}
                          disabled={actionLoading === user.id}
                          data-testid={`reject-user-${user.id}`}
                          className="border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2] rounded-xl text-xs px-3"
                        >
                          <UserX className="w-3.5 h-3.5 mr-1" />
                          Zamítnout
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">
            Všichni uživatelé
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám...</div>
          ) : users.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <TableHead className="text-[#57534E]">Jméno</TableHead>
                  <TableHead className="text-[#57534E]">Email</TableHead>
                  <TableHead className="text-[#57534E]">Role</TableHead>
                  <TableHead className="text-[#57534E]">Stav</TableHead>
                  <TableHead className="text-[#57534E]">Přehledy</TableHead>
                  <TableHead className="text-[#57534E]">Blog</TableHead>
                  <TableHead className="text-[#57534E]">IT</TableHead>
                  <TableHead className="text-[#57534E]">Datum registrace</TableHead>
                  <TableHead className="text-[#57534E] text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const status = statusConfig[user.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  return (
                    <TableRow key={user.id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]" data-testid={`user-row-${user.id}`}>
                      <TableCell className="text-[#1C1917] font-medium flex items-center gap-2">
                        {user.role === 'admin' ? (
                          <Shield className="w-4 h-4 text-[#F59E0B]" />
                        ) : (
                          <User className="w-4 h-4 text-[#94A3B8]" />
                        )}
                        {user.name}
                      </TableCell>
                      <TableCell className="text-[#57534E]">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={user.role === 'admin' ? 'border-[#FDE68A] text-[#92400E] bg-[#FEF3C7]' : 'border-[#E2E8F0] text-[#57534E]'}>
                          {user.role === 'admin' ? 'Admin' : 'Uživatel'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={status.className + ' flex items-center gap-1 w-fit'}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.role !== 'admin' ? (
                          <button
                            onClick={() => handleToggleOverviews(user.id, user.can_view_overviews)}
                            data-testid={`toggle-overviews-${user.id}`}
                            className={`w-9 h-5 rounded-full relative transition-colors ${
                              user.can_view_overviews 
                                ? 'bg-[#16A34A]' 
                                : 'bg-[#CBD5E1]'
                            }`}
                            title={user.can_view_overviews ? 'Odebrat přístup' : 'Udělit přístup'}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              user.can_view_overviews ? 'translate-x-4' : 'translate-x-0.5'
                            }`} />
                          </button>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">vždy</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.role !== 'admin' ? (
                          <button
                            onClick={() => handleToggleBlog(user.id, user.can_write_blog)}
                            data-testid={`toggle-blog-${user.id}`}
                            className={`w-9 h-5 rounded-full relative transition-colors ${
                              user.can_write_blog 
                                ? 'bg-[#16A34A]' 
                                : 'bg-[#CBD5E1]'
                            }`}
                            title={user.can_write_blog ? 'Odebrat oprávnění' : 'Udělit oprávnění'}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              user.can_write_blog ? 'translate-x-4' : 'translate-x-0.5'
                            }`} />
                          </button>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">vždy</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.role !== 'admin' ? (
                          <button
                            onClick={() => handleToggleIT(user.id, user.can_manage_it)}
                            data-testid={`toggle-it-${user.id}`}
                            className={`w-9 h-5 rounded-full relative transition-colors ${
                              user.can_manage_it
                                ? 'bg-[#16A34A]'
                                : 'bg-[#CBD5E1]'
                            }`}
                            title={user.can_manage_it ? 'Odebrat IT správu' : 'Udělit IT správu'}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              user.can_manage_it ? 'translate-x-4' : 'translate-x-0.5'
                            }`} />
                          </button>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">vždy</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#57534E]">
                        {new Date(user.created_at).toLocaleDateString('cs-CZ')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {user.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(user.id)}
                              disabled={actionLoading === user.id}
                              data-testid={`approve-user-main-${user.id}`}
                              className="text-[#16A34A] hover:text-[#15803D] hover:bg-[#F0FDF4]"
                              title="Schválit"
                            >
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                          {user.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReject(user.id)}
                              disabled={actionLoading === user.id}
                              data-testid={`reject-user-main-${user.id}`}
                              className="text-[#F59E0B] hover:text-[#D97706] hover:bg-[#FEF3C7]"
                              title="Zamítnout"
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                          )}
                          {user.status === 'rejected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(user.id)}
                              disabled={actionLoading === user.id}
                              className="text-[#16A34A] hover:text-[#15803D] hover:bg-[#F0FDF4]"
                              title="Znovu schválit"
                            >
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditProfile(user)}
                            data-testid={`edit-profile-${user.id}`}
                            className="text-[#57534E] hover:text-[#16A34A] hover:bg-[#F0FDF4]"
                            title="Upravit profil"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(user.id)}
                              disabled={actionLoading === user.id}
                              data-testid={`delete-user-${user.id}`}
                              className="text-[#57534E] hover:text-red-500 hover:bg-[#FEE2E2]"
                              title="Smazat"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-[#57534E]">
              Zatím nemáte žádné uživatele
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1C1917]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Upravit profil – {editingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Oddělení</Label>
              <Input
                value={profileForm.department}
                onChange={(e) => setProfileForm({ ...profileForm, department: e.target.value })}
                placeholder="např. Marketing"
                data-testid="profile-department-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Pozice</Label>
              <Input
                value={profileForm.position}
                onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                placeholder="např. Manažer"
                data-testid="profile-position-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                placeholder="např. +420 123 456 789"
                data-testid="profile-phone-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Kancelář</Label>
              <Input
                value={profileForm.office}
                onChange={(e) => setProfileForm({ ...profileForm, office: e.target.value })}
                placeholder="např. Budova A, 2. patro"
                data-testid="profile-office-input"
                className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProfileDialogOpen(false)}
              className="border-[#E2E8F0] text-[#57534E] hover:bg-[#F8FAF8]"
            >
              Zrušit
            </Button>
            <Button
              onClick={handleProfileSave}
              disabled={profileSubmitting}
              data-testid="save-profile-btn"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
            >
              {profileSubmitting ? 'Ukládám...' : 'Uložit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminAnnouncementsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [pinVisible, setPinVisible] = useState(false);
  const [pinEditing, setPinEditing] = useState(false);
  const [pinLoading, setPinLoading] = useState(true);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPin = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/announce-pin`, { withCredentials: true });
      setPin(data.pin);
    } catch (error) {
      console.error('Error fetching PIN:', error);
    } finally {
      setPinLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchPin();
  }, []);

  const handleCreate = async () => {
    if (!newText.trim()) {
      toast.error('Zadejte text upozornění');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/announcements`, { text: newText.trim() }, { withCredentials: true });
      toast.success('Upozornění bylo vytvořeno a aktivováno');
      setNewText('');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Nepodařilo se vytvořit upozornění');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await axios.put(`${API}/admin/announcements/${id}/toggle`, {}, { withCredentials: true });
      toast.success(data.active ? 'Upozornění aktivováno' : 'Upozornění deaktivováno');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Nepodařilo se změnit stav');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat toto upozornění?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true });
      toast.success('Upozornění bylo smazáno');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Nepodařilo se smazat upozornění');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Upozornění
      </h2>

      {/* Create new announcement */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">
            Nové upozornění
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Zadejte text upozornění..."
              data-testid="announcement-text-input"
              className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A] flex-1"
            />
            <Button
              onClick={handleCreate}
              disabled={submitting}
              data-testid="create-announcement-btn"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold rounded-xl shadow-sm"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {submitting ? 'Ukládám...' : 'Aktivovat'}
            </Button>
          </div>
          <p className="text-xs text-[#57534E] mt-2">
            Vytvoření nového upozornění automaticky deaktivuje předchozí.
          </p>
        </CardContent>
      </Card>

      {/* List of announcements */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">
            Historie upozornění
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám...</div>
          ) : announcements.length > 0 ? (
            <div className="divide-y divide-[#E2E8F0]">
              {announcements.map((ann) => (
                <div key={ann.id} className="flex items-center justify-between p-4 hover:bg-[#F0FDF4] transition-colors" data-testid={`announcement-row-${ann.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {ann.active ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B] flex-shrink-0 animate-pulse" />
                    ) : (
                      <span className="w-2.5 h-2.5 rounded-full bg-[#CBD5E1] flex-shrink-0" />
                    )}
                    <span className={`text-sm truncate ${ann.active ? 'text-[#1C1917] font-medium' : 'text-[#57534E]'}`}>
                      {ann.text}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <span className="text-xs text-[#57534E]">
                      {new Date(ann.created_at).toLocaleDateString('cs-CZ')}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(ann.id)}
                      data-testid={`toggle-announcement-${ann.id}`}
                      className={ann.active ? 'text-[#F59E0B] hover:text-[#D97706]' : 'text-[#94A3B8] hover:text-[#16A34A]'}
                      title={ann.active ? 'Deaktivovat' : 'Aktivovat'}
                    >
                      {ann.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(ann.id)}
                      data-testid={`delete-announcement-${ann.id}`}
                      className="text-[#57534E] hover:text-red-500"
                      title="Smazat"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-[#57534E]">
              Zatím žádná upozornění
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick announce info + PIN management */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <Key className="w-5 h-5 text-[#F59E0B]" />
            Rychlé upozornění bez přihlášení
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info box */}
          <div className="p-4 rounded-xl bg-[#F0FDF4] border border-[#DCFCE7]" data-testid="quick-announce-info">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#1C1917] space-y-2">
                <p>
                  Upozornění lze nastavit i <strong>bez přihlášení</strong> na adrese:
                </p>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-lg text-[#16A34A] font-mono text-sm" data-testid="quick-announce-url">
                    /upozorneni
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#57534E] hover:text-[#16A34A]"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/upozorneni`);
                      toast.success('URL zkopírována');
                    }}
                    data-testid="copy-announce-url"
                    title="Kopírovat URL"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[#57534E]">
                  Na této stránce stačí zadat PIN kód a text upozornění. Ideální pro rychlou změnu z mobilu nebo když nemáte přístup k administraci.
                </p>
              </div>
            </div>
          </div>

          {/* PIN management */}
          <div className="p-4 rounded-xl bg-[#FFFBEB] border border-[#FDE68A]" data-testid="pin-management">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-[#92400E] flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                PIN kód pro rychlé upozornění
              </span>
            </div>
            {pinLoading ? (
              <p className="text-sm text-[#57534E]">Načítám...</p>
            ) : pinEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Nový PIN (4–6 znaků)"
                  data-testid="new-pin-input"
                  className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#F59E0B] max-w-[200px] text-center tracking-widest"
                />
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/admin/announce-pin`, { pin: newPin }, { withCredentials: true });
                      toast.success('PIN byl změněn');
                      setPin(newPin);
                      setPinEditing(false);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Chyba při změně PIN');
                    }
                  }}
                  disabled={newPin.length < 4}
                  data-testid="save-pin-btn"
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Uložit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setPinEditing(false); setNewPin(''); }}
                  className="text-[#57534E]"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#1C1917] font-mono bg-white px-3 py-1.5 rounded-lg border border-[#E2E8F0]" data-testid="current-pin">
                  {pinVisible ? pin : '••••'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPinVisible(!pinVisible)}
                  className="text-[#57534E] hover:text-[#1C1917]"
                  data-testid="toggle-pin-visibility"
                  title={pinVisible ? 'Skrýt PIN' : 'Zobrazit PIN'}
                >
                  {pinVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setPinEditing(true); setNewPin(pin); }}
                  className="text-[#57534E] hover:text-[#F59E0B]"
                  data-testid="edit-pin-btn"
                  title="Změnit PIN"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminSettingsTab() {
  const [calendarUrl, setCalendarUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/settings/calendar`, { withCredentials: true });
        setCalendarUrl(data.ical_url || '');
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/calendar`, { ical_url: calendarUrl }, { withCredentials: true });
      toast.success('Odkaz na kalendář byl uložen');
    } catch (error) {
      toast.error('Nepodařilo se uložit nastavení');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Kalendář
      </h2>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#16A34A]" />
            Kalendář (iCal)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#57534E]">
            Zadejte odkaz na iCal feed. Události z kalendáře se zobrazí na hlavní stránce.
          </p>
          {loading ? (
            <div className="h-10 bg-[#F0FDF4] rounded-xl animate-pulse" />
          ) : (
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={calendarUrl}
                  onChange={(e) => setCalendarUrl(e.target.value)}
                  placeholder="https://example.com/calendar.ics"
                  data-testid="calendar-url-input"
                  className="pl-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-calendar-url-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl"
              >
                {saving ? 'Ukládám...' : 'Uložit'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Icon mapping for widget catalog display
const ICON_MAP = {
  CloudSun: '☀️', Activity: '📊', BarChart3: '📈', StickyNote: '📝', Cake: '🎂',
  Timer: '⏱️', Clock: '🕐', Headphones: '🎧', ExternalLink: '🔗', Rss: '📰'
};

function AdminWidgetsTab() {
  const [activeWidgets, setActiveWidgets] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWidget, setEditingWidget] = useState(null);
  const [editConfig, setEditConfig] = useState({});

  const fetchData = async () => {
    try {
      const [widgetsRes, catalogRes] = await Promise.all([
        axios.get(`${API}/widgets`, { withCredentials: true }),
        axios.get(`${API}/widgets/catalog`, { withCredentials: true })
      ]);
      setActiveWidgets(widgetsRes.data);
      setCatalog(catalogRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const addWidget = async (type) => {
    try {
      await axios.post(`${API}/admin/widgets`, { type }, { withCredentials: true });
      toast.success('Widget přidán');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const removeWidget = async (id) => {
    try {
      await axios.delete(`${API}/admin/widgets/${id}`, { withCredentials: true });
      toast.success('Widget odebrán');
      fetchData();
    } catch (e) {
      toast.error('Chyba při odebírání');
    }
  };

  const toggleWidget = async (widget) => {
    try {
      await axios.put(`${API}/admin/widgets/${widget.id}`, { enabled: !widget.enabled }, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error('Chyba');
    }
  };

  const saveConfig = async () => {
    if (!editingWidget) return;
    try {
      await axios.put(`${API}/admin/widgets/${editingWidget.id}`, { config: editConfig }, { withCredentials: true });
      toast.success('Nastavení uloženo');
      setEditingWidget(null);
      fetchData();
    } catch (e) {
      toast.error('Chyba při ukládání');
    }
  };

  const moveWidget = async (widgetId, direction) => {
    const idx = activeWidgets.findIndex(w => w.id === widgetId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === activeWidgets.length - 1)) return;
    const newOrder = activeWidgets.map(w => w.id);
    [newOrder[idx], newOrder[idx + direction]] = [newOrder[idx + direction], newOrder[idx]];
    try {
      await axios.put(`${API}/admin/widgets/reorder`, { order: newOrder }, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error('Chyba');
    }
  };

  const activeTypes = activeWidgets.map(w => w.type);
  const availableWidgets = catalog.filter(c => !activeTypes.includes(c.type));

  if (loading) return <div className="text-center py-8 text-[#57534E]">Načítám...</div>;

  const catalogItem = (type) => catalog.find(c => c.type === type);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">Widgety</h2>

      {/* Active widgets */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">
            Aktivní widgety ({activeWidgets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeWidgets.length > 0 ? (
            <div className="space-y-2">
              {activeWidgets.map((widget, idx) => {
                const catItem = catalogItem(widget.type);
                return (
                  <div key={widget.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAF8]" data-testid={`active-widget-${widget.type}`}>
                    <span className="text-lg">{ICON_MAP[widget.icon] || '📦'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#052E16]">{widget.name}</p>
                      <p className="text-[10px] text-[#57534E]">{catItem?.description || ''}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => moveWidget(widget.id, -1)} disabled={idx === 0} className="text-[#57534E] hover:text-[#16A34A] p-1 h-auto">▲</Button>
                      <Button variant="ghost" size="sm" onClick={() => moveWidget(widget.id, 1)} disabled={idx === activeWidgets.length - 1} className="text-[#57534E] hover:text-[#16A34A] p-1 h-auto">▼</Button>
                      {catItem?.configurable && (
                        <Button variant="ghost" size="sm" onClick={() => { setEditingWidget(widget); setEditConfig(widget.config || {}); }} className="text-[#57534E] hover:text-[#F59E0B]" data-testid={`config-widget-${widget.type}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => toggleWidget(widget)} className={widget.enabled ? 'text-[#F59E0B]' : 'text-[#94A3B8]'}>
                        {widget.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeWidget(widget.id)} className="text-[#57534E] hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[#57534E] text-center py-4">Žádné aktivní widgety. Přidejte je ze seznamu níže.</p>
          )}
        </CardContent>
      </Card>

      {/* Widget config editor */}
      {editingWidget && (
        <Card className="bg-white border-[#F59E0B] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-[#F59E0B]" />
              Nastavení: {editingWidget.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(catalogItem(editingWidget.type)?.config_fields || []).map(field => (
              <div key={field.key} className="space-y-1">
                <Label className="text-sm text-[#1C1917]">{field.label}</Label>
                {field.key === 'text' || field.key === 'items' ? (
                  <textarea
                    value={editConfig[field.key] || ''}
                    onChange={(e) => setEditConfig({ ...editConfig, [field.key]: e.target.value })}
                    rows={3}
                    className="w-full bg-white border border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A] rounded-md px-3 py-2 text-sm outline-none resize-none"
                    data-testid={`widget-config-${field.key}`}
                  />
                ) : (
                  <Input
                    value={editConfig[field.key] || ''}
                    onChange={(e) => setEditConfig({ ...editConfig, [field.key]: e.target.value })}
                    placeholder={field.default}
                    data-testid={`widget-config-${field.key}`}
                    className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
                  />
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Button onClick={saveConfig} className="bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="save-widget-config">
                <Save className="w-4 h-4 mr-1" /> Uložit
              </Button>
              <Button variant="outline" onClick={() => setEditingWidget(null)} className="border-[#E2E8F0] text-[#57534E]">
                Zrušit
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available widgets catalog */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">
            Dostupné widgety ({availableWidgets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availableWidgets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableWidgets.map(widget => (
                <div key={widget.type} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] hover:border-[#16A34A]/40 transition-colors" data-testid={`catalog-widget-${widget.type}`}>
                  <span className="text-lg">{ICON_MAP[widget.icon] || '📦'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#052E16]">{widget.name}</p>
                    <p className="text-[10px] text-[#57534E]">{widget.description}</p>
                  </div>
                  <Button size="sm" onClick={() => addWidget(widget.type)} className="bg-[#16A34A] hover:bg-[#15803D] text-white flex-shrink-0" data-testid={`add-widget-${widget.type}`}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#57534E] text-center py-4">Všechny widgety jsou již přidány!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AdminWarehouseTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useState(null);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/warehouse/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) {
      console.error('Error fetching warehouse info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Povolené jsou pouze soubory .xlsx nebo .xls');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/warehouse/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      console.error('Warehouse import error:', error);
      const msg = formatApiErrorDetail(error.response?.data?.detail);
      if (msg) {
        toast.error(msg);
      } else if (error.response) {
        toast.error(`Chyba serveru (${error.response.status}). Zkuste to znovu.`);
      } else if (error.request) {
        toast.error('Nepodařilo se spojit se serverem. Zkontrolujte připojení.');
      } else {
        toast.error('Import se nezdařil. Zkuste to prosím znovu.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Opravdu chcete smazat všechna skladová data? Tato akce je nevratná.')) return;
    setClearing(true);
    try {
      const { data } = await axios.delete(`${API}/admin/warehouse`, { withCredentials: true });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error('Nepodařilo se smazat data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Sklad NES – Import dat
      </h2>

      {/* Upload */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#16A34A]" />
            Nahrát Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-[#57534E]">
              Nahrajte soubor .xlsx s daty skladu NES. Stávající data budou přepsána novým importem.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <label
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-colors ${
                  uploading 
                    ? 'bg-[#CBD5E1] text-[#57534E] cursor-not-allowed' 
                    : 'bg-[#16A34A] hover:bg-[#15803D] text-white shadow-sm'
                }`}
                data-testid="upload-warehouse-btn"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Importuji...' : 'Vybrat soubor a importovat'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="warehouse-file-input"
                />
              </label>
              {info && info.count > 0 && (
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={clearing}
                  data-testid="clear-warehouse-btn"
                  className="border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2]"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {clearing ? 'Mažu...' : 'Smazat všechna data'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current data info */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#16A34A]" />
            Aktuální data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-[#57534E]">Načítám...</p>
          ) : info && info.count > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Počet položek</p>
                  <p className="text-2xl font-bold text-[#052E16]" data-testid="warehouse-item-count">{info.count}</p>
                </div>
                {info.last_import && (
                  <>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Poslední import</p>
                      <p className="text-sm font-medium text-[#1C1917]">
                        {new Date(info.last_import.date).toLocaleString('cs-CZ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Soubor</p>
                      <p className="text-sm font-medium text-[#1C1917]">{info.last_import.filename}</p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-[#94A3B8]">
                Přístup k datům mohou mít uživatelé s oprávněním "Přehledy" (nastavte v záložce Uživatelé).
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Package className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
              <p className="text-sm text-[#57534E]">Zatím nebyla nahrána žádná data.</p>
              <p className="text-xs text-[#94A3B8] mt-1">Importujte Excel soubor výše.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminPurchasesTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/purchases/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) {
      console.error('Error fetching purchases info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Povolené jsou pouze soubory .xlsx nebo .xls');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/purchases/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      console.error('Purchases import error:', error);
      const msg = formatApiErrorDetail(error.response?.data?.detail);
      if (msg) {
        toast.error(msg);
      } else if (error.response) {
        toast.error(`Chyba serveru (${error.response.status}). Zkuste to znovu.`);
      } else {
        toast.error('Import se nezdařil. Zkuste to prosím znovu.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Opravdu chcete smazat všechna data nákupů?')) return;
    setClearing(true);
    try {
      const { data } = await axios.delete(`${API}/admin/purchases`, { withCredentials: true });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error('Nepodařilo se smazat data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Nákup – Import dat
      </h2>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#16A34A]" />
            Nahrát Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-[#57534E]">
              Nahrajte soubor .xlsx s daty nákupů. Očekávané sloupce: Zboží, Název, Množství, Netto fa./MJ, WeekNo.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <label
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-colors ${
                  uploading ? 'bg-[#CBD5E1] text-[#57534E] cursor-not-allowed' : 'bg-[#16A34A] hover:bg-[#15803D] text-white shadow-sm'
                }`}
                data-testid="upload-purchases-btn"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Importuji...' : 'Vybrat soubor a importovat'}
                <input type="file" accept=".xlsx,.xls" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="purchases-file-input" />
              </label>
              {info && info.count > 0 && (
                <Button variant="outline" onClick={handleClear} disabled={clearing} data-testid="clear-purchases-btn" className="border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2]">
                  <Trash2 className="w-4 h-4 mr-2" />
                  {clearing ? 'Mažu...' : 'Smazat všechna data'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#16A34A]" />
            Aktuální data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-[#57534E]">Načítám...</p>
          ) : info && info.count > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-8 flex-wrap">
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Počet záznamů</p>
                  <p className="text-2xl font-bold text-[#052E16]" data-testid="purchases-item-count">{info.count.toLocaleString('cs-CZ')}</p>
                </div>
                {info.last_import && (
                  <>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Týdny</p>
                      <p className="text-sm font-medium text-[#1C1917]">
                        {info.last_import.weeks?.[0]}–{info.last_import.weeks?.[info.last_import.weeks.length - 1]} ({info.last_import.weeks?.length} týdnů)
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Poslední import</p>
                      <p className="text-sm font-medium text-[#1C1917]">{new Date(info.last_import.date).toLocaleString('cs-CZ')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Soubor</p>
                      <p className="text-sm font-medium text-[#1C1917]">{info.last_import.filename}</p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-[#94A3B8]">
                Data jsou dostupná v Přehledy → Nákup pro uživatele s oprávněním.
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
              <p className="text-sm text-[#57534E]">Zatím nebyla nahrána žádná data.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AdminOrganigramTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/organigram/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) {
      console.error('Error fetching organigram info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Povolené jsou pouze soubory PDF');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/organigram`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Organizační struktura byla nahrána');
      fetchInfo();
    } catch (error) {
      console.error('Organigram upload error:', error);
      const msg = formatApiErrorDetail(error.response?.data?.detail);
      if (msg) {
        toast.error(msg);
      } else if (error.response) {
        toast.error(`Chyba serveru (${error.response.status}). Zkuste to znovu.`);
      } else if (error.request) {
        toast.error('Nepodařilo se spojit se serverem. Zkontrolujte připojení.');
      } else {
        toast.error('Nahrání se nezdařilo. Zkuste to prosím znovu.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Organizační struktura (Organigram)
      </h2>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <FileUp className="w-5 h-5 text-[#16A34A]" />
            Nahrát nový organigram
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-[#57534E]">
              Nahrajte soubor PDF s organizační strukturou. Zobrazí se v sekci Adresář.
            </p>
            <label
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-colors ${
                uploading
                  ? 'bg-[#CBD5E1] text-[#57534E] cursor-not-allowed'
                  : 'bg-[#16A34A] hover:bg-[#15803D] text-white shadow-sm'
              }`}
              data-testid="upload-organigram-btn"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Nahrávám...' : 'Vybrat PDF a nahrát'}
              <input
                type="file"
                accept=".pdf"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
                data-testid="organigram-file-input"
              />
            </label>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <Network className="w-5 h-5 text-[#16A34A]" />
            Aktuální organigram
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-[#57534E]">Načítám...</p>
          ) : info?.exists ? (
            <div className="space-y-3">
              <div className="flex items-center gap-8 flex-wrap">
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Soubor</p>
                  <p className="text-sm font-medium text-[#1C1917]">{info.filename}</p>
                </div>
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Platnost od</p>
                  <p className="text-sm font-medium text-[#1C1917] flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-[#16A34A]" />
                    {new Date(info.uploaded_at).toLocaleDateString('cs-CZ')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Velikost</p>
                  <p className="text-sm font-medium text-[#1C1917]">{(info.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
              <p className="text-xs text-[#94A3B8]">
                Organigram je dostupný v sekci Adresář pod tlačítkem "Organizační struktura".
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Network className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
              <p className="text-sm text-[#57534E]">Zatím nebyl nahrán žádný organigram.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminITTab() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#94A3B8');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/it/statuses`, { withCredentials: true });
      setStatuses(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addStatus = async () => {
    if (!newName.trim()) return toast.error('Zadejte název');
    try {
      await axios.post(`${API}/it/statuses`, { name: newName, color: newColor, order: statuses.length }, { withCredentials: true });
      setNewName(''); setNewColor('#94A3B8');
      load();
      toast.success('Status přidán');
    } catch (e) { toast.error(e.response?.data?.detail || 'Chyba'); }
  };

  const updateStatus = async (id, patch) => {
    try {
      await axios.put(`${API}/it/statuses/${id}`, patch, { withCredentials: true });
      load();
    } catch (e) { toast.error('Chyba'); }
  };

  const deleteStatus = async (id, name) => {
    if (!window.confirm(`Smazat status "${name}"?`)) return;
    try {
      await axios.delete(`${API}/it/statuses/${id}`, { withCredentials: true });
      load();
      toast.success('Smazáno');
    } catch (e) { toast.error(e.response?.data?.detail || 'Chyba'); }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">IT — Statusy požadavků</h2>
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">Přidat status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-[#57534E]">Název</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Např. Duplicitní" data-testid="new-status-name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#57534E]">Barva</Label>
              <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} data-testid="new-status-color" className="mt-1 h-10 w-16 rounded border border-[#E2E8F0] cursor-pointer" />
            </div>
            <Button onClick={addStatus} data-testid="add-status-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white">Přidat</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope']">Stávající statusy ({statuses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-[#94A3B8]">Načítám...</p> : (
            <div className="space-y-2">
              {statuses.map(s => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E2E8F0] bg-white" data-testid={`status-row-${s.id}`}>
                  <input
                    type="color"
                    value={s.color}
                    onChange={e => updateStatus(s.id, { color: e.target.value })}
                    className="h-8 w-10 rounded border border-[#E2E8F0] cursor-pointer"
                  />
                  <Input
                    value={s.name}
                    onChange={e => setStatuses(prev => prev.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))}
                    onBlur={e => updateStatus(s.id, { name: e.target.value })}
                    className="flex-1"
                  />
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border" style={{ backgroundColor: s.color + '20', color: s.color, borderColor: s.color + '55' }}>
                    Náhled
                  </span>
                  <Button size="sm" variant="outline" onClick={() => deleteStatus(s.id, s.name)} data-testid={`del-status-${s.id}`} className="border-red-300 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminMaintenanceTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/maintenance/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) {
      console.error('Error fetching maintenance info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Povolené jsou pouze soubory .xlsx nebo .xls');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/maintenance/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      const msg = formatApiErrorDetail(error.response?.data?.detail) || 'Import se nezdařil';
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Opravdu chcete smazat všechna data oprav? Tato akce je nevratná.')) return;
    setClearing(true);
    try {
      const { data } = await axios.delete(`${API}/admin/maintenance`, { withCredentials: true });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error('Nepodařilo se smazat data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#052E16] font-['Manrope']">
        Údržba – Import oprav
      </h2>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#16A34A]" />
            Nahrát Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-[#57534E]">
              Nahrajte .xlsx s exportem servisních úkonů (sloupce: Doklad, Stav, Datum/Čas zahájení i ukončení, Servisované zařízení a název, Popis, Popis úkonu, Pracovník). Stávající data budou přepsána.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <label
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-colors ${
                  uploading
                    ? 'bg-[#CBD5E1] text-[#57534E] cursor-not-allowed'
                    : 'bg-[#16A34A] hover:bg-[#15803D] text-white shadow-sm'
                }`}
                data-testid="upload-maintenance-btn"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Importuji...' : 'Vybrat soubor a importovat'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="maintenance-file-input"
                />
              </label>
              {info && info.count > 0 && (
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={clearing}
                  data-testid="clear-maintenance-btn"
                  className="border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2]"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {clearing ? 'Mažu...' : 'Smazat všechna data'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#16A34A]" />
            Aktuální data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-[#57534E]">Načítám...</p>
          ) : info && info.count > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-8 flex-wrap">
                <div>
                  <p className="text-xs text-[#57534E] uppercase tracking-wide">Počet oprav</p>
                  <p className="text-2xl font-bold text-[#052E16]" data-testid="maintenance-count">{info.count}</p>
                </div>
                {info.last_import && (
                  <>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Poslední import</p>
                      <p className="text-sm font-medium text-[#1C1917]">
                        {new Date(info.last_import.date).toLocaleString('cs-CZ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#57534E] uppercase tracking-wide">Soubor</p>
                      <p className="text-sm font-medium text-[#1C1917]">{info.last_import.filename}</p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-[#94A3B8]">
                Data zobrazují uživatelé s oprávněním "Přehledy" na stránce Údržba.
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Wrench className="w-10 h-10 mx-auto mb-2 text-[#CBD5E1]" />
              <p className="text-sm text-[#57534E]">Zatím nebyla nahrána žádná data.</p>
              <p className="text-xs text-[#94A3B8] mt-1">Importujte Excel soubor výše.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminVacationTab() {
  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ hr_emails: [] });
  const [newDept, setNewDept] = useState('');
  const [newType, setNewType] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#16A34A');
  const [newHrEmail, setNewHrEmail] = useState('');

  const fetchAll = async () => {
    try {
      const [dRes, tRes, uRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/departments`, { withCredentials: true }),
        axios.get(`${API}/leave-types`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/vacation-settings`, { withCredentials: true }),
      ]);
      setDepartments(dRes.data);
      setLeaveTypes(tRes.data);
      setUsers(uRes.data);
      setSettings(sRes.data || { hr_emails: [] });
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAll(); }, []);

  const addDept = async () => {
    if (!newDept.trim()) return;
    await axios.post(`${API}/admin/departments`, { name: newDept }, { withCredentials: true });
    setNewDept('');
    toast.success('Oddělení přidáno');
    fetchAll();
  };

  const deleteDept = async (id) => {
    await axios.delete(`${API}/admin/departments/${id}`, { withCredentials: true });
    toast.success('Smazáno');
    fetchAll();
  };

  const setSupervisor = async (deptId, userId) => {
    await axios.put(`${API}/admin/departments/${deptId}`, { supervisor_id: userId }, { withCredentials: true });
    toast.success('Nadřízený nastaven');
    fetchAll();
  };

  const setUserDept = async (userId, deptId) => {
    await axios.put(`${API}/admin/users/${userId}/department`, { department_id: deptId }, { withCredentials: true });
    toast.success('Oddělení přiřazeno');
    fetchAll();
  };

  const addLeaveType = async () => {
    if (!newType.trim()) return;
    await axios.post(`${API}/admin/leave-types`, { name: newType, color: newTypeColor }, { withCredentials: true });
    setNewType('');
    toast.success('Typ přidán');
    fetchAll();
  };

  const deleteLeaveType = async (id) => {
    await axios.delete(`${API}/admin/leave-types/${id}`, { withCredentials: true });
    toast.success('Smazáno');
    fetchAll();
  };

  const addHrEmail = async () => {
    if (!newHrEmail.trim()) return;
    const updated = { ...settings, hr_emails: [...(settings.hr_emails || []), newHrEmail.trim()] };
    await axios.put(`${API}/admin/vacation-settings`, updated, { withCredentials: true });
    setNewHrEmail('');
    setSettings(updated);
    toast.success('HR kontakt přidán');
  };

  const removeHrEmail = (email) => {
    const updated = { ...settings, hr_emails: (settings.hr_emails || []).filter(e => e !== email) };
    axios.put(`${API}/admin/vacation-settings`, updated, { withCredentials: true });
    setSettings(updated);
  };

  return (
    <div className="space-y-4">
      {/* Departments */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader><CardTitle className="font-['Manrope'] text-[#052E16]">Oddělení</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newDept} onChange={e => setNewDept(e.target.value)} placeholder="Název oddělení..." data-testid="new-dept-input" className="flex-1" />
            <Button onClick={addDept} data-testid="add-dept-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white">Přidat</Button>
          </div>
          {departments.map(d => (
            <div key={d.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
              <div>
                <span className="font-medium text-[#1C1917]">{d.name}</span>
                <span className="text-xs text-[#94A3B8] ml-2">
                  Nadřízený: {users.find(u => u.id === d.supervisor_id)?.name || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1"
                  value={d.supervisor_id || ''}
                  onChange={e => setSupervisor(d.id, e.target.value || null)}
                  data-testid={`dept-supervisor-${d.id}`}
                >
                  <option value="">Bez nadřízeného</option>
                  {users.filter(u => u.status === 'approved').map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <Button variant="ghost" size="sm" onClick={() => deleteDept(d.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* User-Department assignment */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader><CardTitle className="font-['Manrope'] text-[#052E16]">Přiřazení uživatelů k oddělení</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.filter(u => u.status === 'approved').map(u => (
              <div key={u.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F8FAFC]">
                <span className="text-sm text-[#1C1917]">{u.name} <span className="text-[#94A3B8]">({u.email})</span></span>
                <select
                  className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1"
                  value={u.department_id || ''}
                  onChange={e => setUserDept(u.id, e.target.value || null)}
                  data-testid={`user-dept-${u.id}`}
                >
                  <option value="">Bez oddělení</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Leave Types */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader><CardTitle className="font-['Manrope'] text-[#052E16]">Typy dovolené</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newType} onChange={e => setNewType(e.target.value)} placeholder="Název typu..." data-testid="new-type-input" className="flex-1" />
            <input type="color" value={newTypeColor} onChange={e => setNewTypeColor(e.target.value)} className="w-10 h-10 rounded-lg border border-[#E2E8F0] cursor-pointer" />
            <Button onClick={addLeaveType} data-testid="add-type-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white">Přidat</Button>
          </div>
          {leaveTypes.map(lt => (
            <div key={lt.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: lt.color }} />
                <span className="font-medium text-[#1C1917]">{lt.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => deleteLeaveType(lt.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* HR Contacts */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader><CardTitle className="font-['Manrope'] text-[#052E16]">HR notifikace (emaily)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#57534E]">Tyto emaily obdrží notifikaci při schválení nebo zamítnutí dovolené.</p>
          <div className="flex gap-2">
            <Input value={newHrEmail} onChange={e => setNewHrEmail(e.target.value)} placeholder="hr@firma.cz" data-testid="new-hr-email" className="flex-1" />
            <Button onClick={addHrEmail} data-testid="add-hr-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white">Přidat</Button>
          </div>
          {(settings.hr_emails || []).map(e => (
            <div key={e} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg">
              <span className="text-sm">{e}</span>
              <Button variant="ghost" size="sm" onClick={() => removeHrEmail(e)} className="text-red-500 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}



function AdminPruvodkyTab() {
  const [info, setInfo] = useState(null);
  const [kalkInfo, setKalkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingKalk, setUploadingKalk] = useState(false);

  const fetchInfo = async () => {
    try {
      const [pruv, kalk] = await Promise.all([
        axios.get(`${API}/admin/pruvodky/info`, { withCredentials: true }),
        axios.get(`${API}/admin/kalkulace/info`, { withCredentials: true }),
      ]);
      setInfo(pruv.data);
      setKalkInfo(kalk.data);
    } catch (error) {
      console.error('Error fetching info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/pruvodky/import`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při importu');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleUploadKalk = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKalk(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/kalkulace/import`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při importu');
    } finally {
      setUploadingKalk(false);
      e.target.value = '';
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Opravdu smazat všechna data průvodek?')) return;
    try {
      await axios.delete(`${API}/admin/pruvodky`, { withCredentials: true });
      toast.success('Data smazána');
      fetchInfo();
    } catch {
      toast.error('Nepodařilo se smazat data');
    }
  };

  const handleClearKalk = async () => {
    if (!window.confirm('Opravdu smazat všechny kalkulace?')) return;
    try {
      await axios.delete(`${API}/admin/kalkulace`, { withCredentials: true });
      toast.success('Kalkulace smazány');
      fetchInfo();
    } catch {
      toast.error('Nepodařilo se smazat data');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-[#052E16] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Průvodky – Import dat
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#57534E]">
            Nahrajte Excel s průvodkami výroby. Data se zobrazí v Přehledy &rarr; Průvodky.
          </p>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="pruvodky-upload-input" />
              <Button asChild disabled={uploading} data-testid="pruvodky-upload-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Importuji...' : 'Nahrát Excel'}
                </span>
              </Button>
            </label>
            {info?.count > 0 && (
              <Button variant="outline" size="sm" onClick={handleClear} data-testid="pruvodky-clear-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Smazat data
              </Button>
            )}
          </div>
          {info?.last_import && (
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 text-sm">
              <p><strong>Poslední import:</strong> {info.last_import.filename}</p>
              <p><strong>Záznamy:</strong> {info.last_import.rows} ({info.last_import.products} produktů)</p>
              <p><strong>Datum:</strong> {new Date(info.last_import.date).toLocaleString('cs-CZ')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-[#052E16] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Kalkulace – Import dat
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#57534E]">
            Nahrajte Excel s kalkulacemi. Data se spárují s průvodkami podle názvu produktu.
          </p>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUploadKalk} disabled={uploadingKalk} data-testid="kalkulace-upload-input" />
              <Button asChild disabled={uploadingKalk} data-testid="kalkulace-upload-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingKalk ? 'Importuji...' : 'Nahrát kalkulace'}
                </span>
              </Button>
            </label>
            {kalkInfo?.count > 0 && (
              <Button variant="outline" size="sm" onClick={handleClearKalk} data-testid="kalkulace-clear-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Smazat
              </Button>
            )}
          </div>
          {kalkInfo?.last_import && (
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 text-sm">
              <p><strong>Poslední import:</strong> {kalkInfo.last_import.filename}</p>
              <p><strong>Záznamy:</strong> {kalkInfo.last_import.rows} ({kalkInfo.last_import.products} produktů)</p>
              <p><strong>Datum:</strong> {new Date(kalkInfo.last_import.date).toLocaleString('cs-CZ')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminL1DashboardTab() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/l1dashboard/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) {
      console.error('Error fetching L1 dashboard info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/l1dashboard/import`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      fetchInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při importu');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Opravdu smazat všechna data L1 Dashboardu?')) return;
    try {
      await axios.delete(`${API}/admin/l1dashboard`, { withCredentials: true });
      toast.success('Data smazána');
      fetchInfo();
    } catch {
      toast.error('Nepodařilo se smazat data');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-[#052E16] flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            L1 Dashboard - Import dat
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#57534E]">
            Nahrajte Excel se strukturou Weekly Dashboard (List1). Data se zobrazí v Přehledy &rarr; Dashboard.
          </p>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="l1dashboard-upload-input" />
              <Button asChild disabled={uploading} data-testid="l1dashboard-upload-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Importuji...' : 'Nahrát Excel'}
                </span>
              </Button>
            </label>
            {info?.count > 0 && (
              <Button variant="outline" size="sm" onClick={handleClear} data-testid="l1dashboard-clear-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Smazat data
              </Button>
            )}
          </div>
          {info?.last_import && (
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 text-sm">
              <p><strong>Poslední import:</strong> {info.last_import.filename}</p>
              <p><strong>Týdny:</strong> {info.last_import.weeks} ({info.last_import.week_range?.[0]}-{info.last_import.week_range?.[1]})</p>
              <p><strong>Datum:</strong> {new Date(info.last_import.date).toLocaleString('cs-CZ')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-page">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope']">
            Administrace
          </h1>
          <p className="text-[#57534E] mt-2">
            Správa intranetu
          </p>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-[#F8FAF8] border border-[#E2E8F0] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger 
              value="users" 
              data-testid="tab-users"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Uživatelé
            </TabsTrigger>
            <TabsTrigger 
              value="bubbles" 
              data-testid="tab-bubbles"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Rozcestník
            </TabsTrigger>
            <TabsTrigger 
              value="blog" 
              data-testid="tab-blog"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Blog
            </TabsTrigger>
            <TabsTrigger 
              value="announcements" 
              data-testid="tab-announcements"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Upozornění
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              data-testid="tab-settings"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Kalendář
            </TabsTrigger>
            <TabsTrigger 
              value="widgets" 
              data-testid="tab-widgets"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Widgety
            </TabsTrigger>
            <TabsTrigger 
              value="warehouse" 
              data-testid="tab-warehouse"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Sklad
            </TabsTrigger>
            <TabsTrigger 
              value="organigram" 
              data-testid="tab-organigram"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Organigram
            </TabsTrigger>
            <TabsTrigger 
              value="purchases" 
              data-testid="tab-purchases"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Nákup
            </TabsTrigger>
            <TabsTrigger 
              value="l1dashboard" 
              data-testid="tab-l1dashboard"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              L1 Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="pruvodky" 
              data-testid="tab-pruvodky"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Průvodky
            </TabsTrigger>
            <TabsTrigger 
              value="vacation" 
              data-testid="tab-vacation"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Dovolená
            </TabsTrigger>
            <TabsTrigger 
              value="maintenance" 
              data-testid="tab-maintenance"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Údržba
            </TabsTrigger>
            <TabsTrigger 
              value="it" 
              data-testid="tab-it"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              IT
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <AdminUsersTab />
          </TabsContent>

          <TabsContent value="bubbles">
            <AdminBubblesTab />
          </TabsContent>
          
          <TabsContent value="blog">
            <AdminBlogTab />
          </TabsContent>

          <TabsContent value="announcements">
            <AdminAnnouncementsTab />
          </TabsContent>

          <TabsContent value="settings">
            <AdminSettingsTab />
          </TabsContent>

          <TabsContent value="widgets">
            <AdminWidgetsTab />
          </TabsContent>

          <TabsContent value="warehouse">
            <AdminWarehouseTab />
          </TabsContent>

          <TabsContent value="organigram">
            <AdminOrganigramTab />
          </TabsContent>

          <TabsContent value="purchases">
            <AdminPurchasesTab />
          </TabsContent>

          <TabsContent value="l1dashboard">
            <AdminL1DashboardTab />
          </TabsContent>

          <TabsContent value="pruvodky">
            <AdminPruvodkyTab />
          </TabsContent>

          <TabsContent value="vacation">
            <AdminVacationTab />
          </TabsContent>

          <TabsContent value="maintenance">
            <AdminMaintenanceTab />
          </TabsContent>

          <TabsContent value="it">
            <AdminITTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
